import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Spinner, ErrorLocal, HoverMenu, useSnackbar } from 'components';
import { useMutation, useQuery } from '@apollo/client';
import { useEventContext, useQuizContext, useQuizSessionContext, useUserContext } from 'contexts';
import { getJeopardyQuizConfig } from 'graphql/queries';
import { createQuizSession } from 'graphql/mutations';
import { CreateQuizSession, CreateQuizSessionVariables } from 'graphql/mutations/types/CreateQuizSession';
import { SessionTypes } from 'consts';
import { NavPages, NavUtils } from 'utils';
import { useNavigate, useParams } from 'react-router-dom';
import { JeopardyProvider } from './jeopardy-context';
import { JeopardyPlayerView } from './jeopardy-player-view';
import { getJeopardyThemeVars } from './jeopardy-theme';
import { JeopardyBoardConfig } from './jeopardy-types';
import { getJeopardyBoardConfigValidationErrors } from './jeopardy-config-validation';
import { getCssStylesFromQuizSettings } from 'utils/style-utils';
import './jeopardy-quiz.css';

/**
 * Public player view for a Jeopardy session.
 * Subscribes to game state via JeopardyProvider and renders the read-only player screen.
 * This component is rendered at the quiz session play route (no URL suffix).
 */
export const JeopardyBase = () => {
    const { quiz } = useQuizContext();
    const event = useEventContext();
    const user = useUserContext();
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const { eventAlias } = useParams();
    const sessionContext = useQuizSessionContext();
    const theme = useTheme();
    const jeopardyThemeVars = React.useMemo(() => getJeopardyThemeVars(theme), [theme]);
    const { slideCustomBackgroundStyles, slideCustomFontStyles, customTitlePageStyles } = getCssStylesFromQuizSettings(quiz.styles);

    const quizId = quiz.id;
    const sessionId = sessionContext?.session?.id;
    const sessionKey = sessionContext?.session?.key;
    const canManageQuiz = quizId !== undefined && user.isQuizCreatorOrEventAdmin(quizId, eventAlias);

    const [createSession, { loading: creatingSession }] = useMutation<CreateQuizSession, CreateQuizSessionVariables>(
        createQuizSession
    );

    const { data, loading, error } = useQuery(getJeopardyQuizConfig, {
        variables: { quizId: quizId! },
        skip: quizId === undefined,
    });

    const onCreateSession = async () => {
        try {
            const result = await createSession({
                variables: {
                    creatorId: user.id,
                    isOpen: true,
                    key: crypto.randomUUID().replaceAll('-', ''),
                    quizId,
                    type: SessionTypes.Distributed,
                },
            });

            const createdKey = result.data?.insert_quiz_master_quiz_session?.returning.at(0)?.key;
            if (!createdKey) {
                snackbar.error('Failed to create Jeopardy session. Please try again.');
                return;
            }

            navigate(NavUtils.generateNavPath(NavPages.QuizSession, quizId, event?.alias, createdKey));
        } catch (mutationError) {
            const message = mutationError instanceof Error ? mutationError.message : 'Unknown session creation error';
            snackbar.error('Failed to create Jeopardy session: ' + message);
        }
    };

    const onOpenOverview = () => {
        if (!sessionKey) return;

        window.open(
            NavUtils.generateAbsolutePath(NavPages.QuizSessionOverview, quizId, event?.alias, sessionKey),
            '_blank'
        );
    };

    const onExitQuiz = () => {
        navigate(NavUtils.getQuizSelectorPath(event?.alias));
    };

    if (loading) return <Spinner />;
    if (error) return <ErrorLocal errorMessage="Error loading Jeopardy board." errorDetails={error} />;

    const configRows = data?.quiz_master_quiz_settings ?? [];
    const boardConfig: JeopardyBoardConfig | null = configRows[0]?.additional_config ?? null;
    const boardValidationErrors = getJeopardyBoardConfigValidationErrors(boardConfig);
    const hasValidBoardConfig = boardValidationErrors.length === 0;

    if (!sessionId) {
        const handleCreateSession = async () => {
            if (!hasValidBoardConfig) {
                snackbar.error('Cannot create Jeopardy session: board configuration is incomplete.');
                return;
            }

            await onCreateSession();
        };

        return (
            <div style={{ ...jeopardyThemeVars, ...customTitlePageStyles, ...slideCustomFontStyles }}>
                <div className="jeopardy-panel" style={{ minHeight: '100vh', padding: 24, display: 'grid', placeItems: 'center' }}>
                    <div
                        style={{
                            width: 'min(760px, 96vw)',
                            background: 'var(--j-surface)',
                            border: '1px solid var(--j-border)',
                            borderRadius: 12,
                            padding: 24,
                            boxShadow: 'var(--j-shadow)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                        }}
                    >
                        <h2 style={{ margin: 0, color: 'var(--j-gold)', textTransform: 'uppercase', letterSpacing: 2 }}>
                            Jeopardy Session Setup
                        </h2>
                        <p style={{ margin: 0, color: 'var(--j-muted)' }}>
                            Create a live Jeopardy session, then open Admin Overview to define teams, timer settings, and start the game.
                        </p>
                        {!boardConfig && (
                            <div className="jeopardy-lobby__no-board" style={{ width: 'fit-content' }}>
                                Board config missing. Configure the board in quiz settings before starting.
                            </div>
                        )}
                        {boardConfig && !hasValidBoardConfig && (
                            <div className="jeopardy-lobby__no-board" style={{ width: 'fit-content' }}>
                                Board config has validation warnings and cannot be used to start a game yet.
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                            {canManageQuiz && (
                                <button
                                    type="button"
                                    className="jeopardy-overview__nav-btn"
                                    onClick={handleCreateSession}
                                    disabled={creatingSession || !hasValidBoardConfig}
                                >
                                    {creatingSession ? 'Creating Session...' : 'Create Jeopardy Session'}
                                </button>
                            )}
                            {canManageQuiz && (
                                <button
                                    type="button"
                                    className="jeopardy-overview__advance-btn"
                                    onClick={() => navigate(NavUtils.generateNavPath(NavPages.EditQuiz, quizId, event?.alias))}
                                >
                                    Edit Board Configuration
                                </button>
                            )}
                            <button type="button" className="jeopardy-overview__reset-btn" onClick={onExitQuiz}>
                                Back to Quiz List
                            </button>
                        </div>
                        {!canManageQuiz && (
                            <p style={{ margin: 0, color: 'var(--j-red)', fontSize: '0.9rem' }}>
                                Only the quiz creator or event admin can create Jeopardy sessions.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (!boardConfig) {
        return (
            <div className="jeopardy-base__no-board">
                <p>This Jeopardy quiz has no board configured yet.</p>
                <p>The quiz admin needs to configure the board in quiz settings before a game can be played.</p>
            </div>
        );
    }

    const overviewUrl = sessionKey
        ? NavUtils.generateAbsolutePath(NavPages.QuizSessionOverview, quizId, event?.alias, sessionKey)
        : undefined;
    const shareUrl = sessionKey
        ? NavUtils.generateAbsolutePath(NavPages.QuizSession, quizId, event?.alias, sessionKey)
        : undefined;

    return (
        <div style={{ ...jeopardyThemeVars, height: '100%', ...slideCustomBackgroundStyles, ...slideCustomFontStyles }}>
            <JeopardyProvider sessionId={sessionId}>
                <div style={{ position: 'relative', height: '100%' }}>
                    <HoverMenu
                        onCloseClick={onExitQuiz}
                        onOpenAdminOverview={canManageQuiz ? onOpenOverview : undefined}
                    />
                    <JeopardyPlayerView
                        boardConfig={boardConfig}
                        isQuizMaster={canManageQuiz}
                        overviewUrl={overviewUrl}
                        shareUrl={shareUrl}
                    />
                </div>
            </JeopardyProvider>
        </div>
    );
};
